import SortableContainer from '@/Components/dnd/SortableContainer';
import { useEditorContext } from '@/Components/page-ui-editor-2';
import { usePanelBreakpoint } from '@/Components/page-ui-editor-2/panel';
import RenderIfVisible from '@/Utility/RenderIfVisible';
import { useBreakpoints } from '@/Utility/hooks';
import type { ZnglyVideo } from '@/graphql/types';
import { useAppStore } from '@/inertia/AppStore';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Box, type BoxProps, LinearProgress, List, type ListProps, Stack, Typography } from '@mui/material';
import ListViewItem, { listRIVProps, SORTABLE_LIST_ITEM_DND_PREFIX } from '../../Catalog/ListItem';
import { SIDEBAR_LIST_ITEM_PREFIX } from '../../Catalog/catalog.utils';
import FormGating from '../../FormGating';
import GridView from '../GridView';
import Bar from './Bar';

import './SideBar.less';
import { useMemo } from 'react';

type Props = {
  isFallback?: boolean;
  /**
   * props to be slotted into the SideBar component
   */
  slotProps?: {
    SideBar?: BoxProps;
    List?: ListProps | BoxProps;
  };
};

export const SideBar = (props: Props) => {
  const nodes = useAppStore((state) => state?.catalogNodes);
  const playerNode = useAppStore((state) => state?.playerNode);
  const { isMobile } = usePanelBreakpoint('left');

  const catalogLoadMore = useAppStore((state) => state?.catalogLoadMore);
  const isCatalogLoadingMore = useAppStore((state) => state?.isCatalogLoadingMore);

  return (
    <Box className="SideBar" {...props?.slotProps?.SideBar}>
      <FormGating location="sidebar" />
      <Bar isFallback={props?.isFallback} />
      {isMobile ? (
        <GridView showBar={false} showShare={false} isFallback={props?.isFallback} />
      ) : (
        <div className="ListWrapper FormGating_List">
          <List className="List" {...(props?.slotProps?.List as ListProps)}>
            {isCatalogLoadingMore && (
              <LinearProgress sx={{ width: '100%', height: '3px', position: 'sticky', top: '0', zIndex: 100 }} />
            )}
            {nodes?.map((node, idx) => {
              const isMainNode = node?.id === playerNode?.id;
              const isLastNode = idx === nodes?.length - 1;

              const key = `sidebar_item__${encodeURIComponent(window.location.pathname)}_${node?.id}`;

              return (
                <RenderIfVisible
                  key={`sidebar_rif_${node?.id}_${idx}`}
                  onChange={(isVisible: boolean) => {
                    if (isLastNode && isVisible) {
                      catalogLoadMore?.();
                    }
                  }}
                  rootElementClass={`renderIfVisible ${SIDEBAR_LIST_ITEM_PREFIX}${node?.id?.replaceAll('=', '')}`}
                  {...listRIVProps(node, idx)}
                >
                  <ListViewItem key={key} node={node} disabled={isMainNode} idx={idx} isFallback={props?.isFallback} />
                </RenderIfVisible>
              );
            })}
          </List>
        </div>
      )}
    </Box>
  );
};

const SORTABLE_CONTAINER_ID = 'sidebar-sorting-container';

type SideBarSortableProps = Props & {
  nodes: ZnglyVideo[];
  onDragEnd?: (newNodes: ZnglyVideo[], source: number, destination: number) => void;
  onRemove?: (node: ZnglyVideo) => void;
  sortingDisabled?: boolean;
};

export const SideBarSortable = (props: SideBarSortableProps) => {
  const playerNode = useAppStore((state) => state?.playerNode);
  const { isMobile } = useBreakpoints();

  const editorCtx = useEditorContext();

  const sortingDisabled = useMemo(() => {
    if (editorCtx?.config?.readOnly) {
      return true;
    }

    if (props?.sortingDisabled) {
      return props?.sortingDisabled;
    }

    return false;
  }, []);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!props?.onDragEnd) {
      return;
    }

    const { nodes, onDragEnd } = props;

    if (!nodes?.length) {
      return;
    }

    if (
      !active.id?.toString()?.startsWith(SORTABLE_LIST_ITEM_DND_PREFIX) ||
      !over?.id?.toString()?.startsWith(SORTABLE_LIST_ITEM_DND_PREFIX)
    ) {
      return;
    }

    if (active?.id === over?.id) {
      return;
    }

    const oldIndex = nodes.findIndex((item) => item?.id === active?.data?.current?.node?.id);
    const newIndex = nodes.findIndex((item) => item?.id === over?.data?.current?.node?.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    if (oldIndex !== newIndex) {
      const newNodes = arrayMove(nodes as ZnglyVideo[], oldIndex, newIndex);

      onDragEnd(newNodes, oldIndex, newIndex);
    }
  }

  return (
    <Box className="SideBar" {...props?.slotProps?.SideBar}>
      <FormGating location="sidebar" />
      <Bar isFallback={props?.isFallback} />
      {isMobile ? (
        <GridView showBar={false} showShare={false} isFallback={props?.isFallback} />
      ) : (
        <div className="ListWrapper FormGating_List">
          <SortableContainer
            dndContextProps={{
              onDragEnd: handleDragEnd,
            }}
            sortableContextProps={{
              id: SORTABLE_CONTAINER_ID,
              items: props.nodes?.map((item) => SORTABLE_LIST_ITEM_DND_PREFIX + item?.id) ?? [],
              disabled: props?.sortingDisabled,
            }}
            boxProps={{
              className: 'List',
              sx: {
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              },
              ...(props?.slotProps?.List as BoxProps),
            }}
          >
            {props.nodes?.map((node, idx) => {
              const isMainNode = node?.id === playerNode?.id;

              const key = `sidebar_item__${encodeURIComponent(window.location.pathname)}_${node?.id}`;

              return (
                <div
                  key={`sidebar_rif_${node?.id}_${idx}`}
                  className={`renderIfVisible ${SIDEBAR_LIST_ITEM_PREFIX}${node?.id?.replaceAll('=', '')}`}
                >
                  <ListViewItem
                    useSortable={!sortingDisabled}
                    key={key}
                    node={node as ZnglyVideo}
                    disabled={isMainNode}
                    idx={idx}
                    isFallback={props?.isFallback}
                    onRemove={
                      !sortingDisabled && props?.onRemove
                        ? () => {
                            props?.onRemove?.(node);
                          }
                        : undefined
                    }
                  />
                </div>
              );
            })}
            {!props.nodes?.length && (
              <Stack sx={{ minHeight: 300 }} alignItems="center" p={1}>
                <Typography variant="caption">+ Click on this box then drag and drop from the right</Typography>
              </Stack>
            )}
          </SortableContainer>
        </div>
      )}
    </Box>
  );
};

export default SideBar;
